import { useEffect, useState } from "react";
import { Button, Col, FormGroup, Input, Label, Row, Table } from "reactstrap";
import Select, { components } from "react-select";
import CreatableSelect from "react-select/creatable";
import { IoMdClose } from "react-icons/io";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import FixedSave from "../../../../components/Actions/FixedSave";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { get, post } from "../../../../utils/helpers/api";
import { toast } from "react-toastify";
import SelectCustomStyles from "../../../../utils/styles/reactSelect";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns";
import { useSettings } from "../../../../context/settings";
import CustomSelect from "../../../../components/Forms/CustomSelect";
import i18n from "i18next";

const CustomerMenuList = (props) => (
  <components.MenuList {...props}>
    {props.children}
    <div
      onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}
      onClick={() => window.open('/crm/customers/create', '_blank')}
      style={{ padding: '8px 12px', cursor: 'pointer', color: '#0d6efd', borderTop: '1px solid #e0e0e0', fontSize: 14 }}
    >
      + {i18n.t('common.createNewCustomer')}
    </div>
  </components.MenuList>
);

function CreateRecurringInvoicePage() {
  const { t } = useTranslation();
  const { datePickerFormat } = useSettings();
  const { state } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [initLoading, setInitLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [customersData, setCustomersData] = useState([]);
  const [companySettings, setCompanySettings] = useState({});
  const [invoiceData, setInvoiceData] = useState({
    customer_id: state?.invoiceData?.customer_id || "",
    invoice_number: state?.invoiceData?.invoice_number || "",
    start_date: state?.invoiceData?.start_date ? parseISO(state.invoiceData.start_date) : new Date(),
    end_date: state?.invoiceData?.end_date ? parseISO(state.invoiceData.end_date) : new Date(new Date().setDate(new Date().getDate() + 365)),
    frequency: state?.invoiceData?.frequency || "",
    sales_person_id: state?.invoiceData?.sales_person_id || null,
    notes: state?.invoiceData?.notes || '',
  });
  const [rows, setRows] = useState(
    state?.invoiceData?.items?.length > 0 ? state.invoiceData.items.map((item, index) => ({
      id: Date.now() + index,
      item: item.product_name,
      description: item.description,
      quantity: item.quantity,
      unitPrice: item.unit_price,
      total: item.quantity * item.unit_price
    })) : [{
      id: Date.now(),
      item: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      total: 0
    }]
  );

  useEffect(() => {
    initData();
  }, []);

  const initData = async () => {
    setInitLoading(true);
    const data = await get('/sales/recurring-invoices/create');

    if(data.success) {
      setCustomersData(data.customers);
      setCompanySettings(data.company_settings);
      if(invoiceData?.invoice_number === "") {
        setInvoiceData(prev => ({
          ...prev,
          invoice_number: data.invoice_number
        }));
      }
      setInitLoading(false);
    }
  }

  const handleAddRow = () => {
    setRows([...rows, {
      id: Date.now(),
      item: '',
      description: '',
      quantity: 0,
      unitPrice: 0,
      total: 0
    }]);
  };

  const handleDeleteRow = (index) => {
    const newRows = rows.filter((_, i) => i !== index);
    setRows(newRows);
  };

  const saveInvoice = async () => {
    setSaveLoading(true);

    const url = id ? '/sales/recurring-invoices/update/' + id : '/sales/recurring-invoices/store';
    const data = await post(url, {
      ...invoiceData,
      start_date: invoiceData.start_date ? format(invoiceData.start_date, 'yyyy-MM-dd') : null,
      end_date: invoiceData.end_date ? format(invoiceData.end_date, 'yyyy-MM-dd') : null,
      items: rows
    });

    if(data.success) {
      navigate('/sales/recurring-invoices');
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setSaveLoading(false);
  }

  return(
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[t('sales.recurringInvoices'), id ? t('sales.editRecurringInvoice') : t('sales.newRecurring')]}
          onBack={() => navigate('/sales/recurring-invoices')}
        />
        <div className="subheader-page-content scrollable">
          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.customerLabel')}: </Label>
              <Select
                options={customersData.map(customer => ({ value: customer.id, label: customer.company_name || customer.firstname + ' ' + customer.lastname }))}
                styles={SelectCustomStyles}
                value={customersData.map(c => ({ value: c.id, label: c.company_name || c.firstname + ' ' + c.lastname })).find(o => o.value === invoiceData.customer_id) || null}
                onChange={selected => setInvoiceData({ ...invoiceData, customer_id: selected ? selected.value : null })}
                components={{ MenuList: CustomerMenuList }}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.startDate')}: </Label>
              <DatePicker
                selected={invoiceData.start_date}
                onChange={(d) => setInvoiceData({ ...invoiceData, start_date: d })}
                className="form-control"
                dateFormat={datePickerFormat}
                minDate={new Date()}
              />
            </Col>
            <Col md="6" className="mb-3">
              <Label>{t('sales.endDate')}: </Label>
              <DatePicker
                selected={invoiceData.end_date}
                onChange={(d) => setInvoiceData({ ...invoiceData, end_date: d })}
                className="form-control"
                dateFormat={datePickerFormat}
                minDate={invoiceData.start_date}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.repeatEvery')} </Label>
              <Select
                options={[
                  { value: 'daily', label: t('sales.daily') },
                  { value: 'weekly', label: t('sales.weekly') },
                  { value: 'monthly', label: t('sales.monthly') },
                  { value: 'yearly', label: t('sales.yearly') },
                  { value: 'custom', label: t('common.custom') }
                ]}
                styles={SelectCustomStyles}
                value={[{ value: 'daily', label: t('sales.daily') }, { value: 'weekly', label: t('sales.weekly') }, { value: 'monthly', label: t('sales.monthly') }, { value: 'yearly', label: t('sales.yearly') }, { value: 'custom', label: t('common.custom') }].find(o => o.value === invoiceData.frequency) || null}
                onChange={selected => setInvoiceData({ ...invoiceData, frequency: selected ? selected.value : null })}
              />
            </Col>
          </Row>

          <Table bordered>
            <thead>
              <tr>
                <th>{t('common.item')}</th>
                <th>{t('common.description')}</th>
                <th width="100px">{t('common.quantity')}</th>
                <th width="100px">{t('common.unitPrice')}</th>
                <th width="140px">{t('common.total')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((data, key) => (
                <tr key={key}>
                  <td>
                    <CustomSelect
                      value={data.product_name ? { value: data.product_id, label: data.product_name } : null}
                      onChange={(selected, objectData) => {
                        setRows(prev => prev.map(r => {
                          if (r.id !== data.id) return r;
                          const unitPrice = objectData?.selling_price ?? r.unitPrice;
                          return {
                            ...r,
                            product_id: selected ? selected.value : null,
                            product_name: selected ? selected.label : '',
                            unitPrice,
                            total: parseFloat(r.quantity || 0) * parseFloat(unitPrice || 0) * (1 - parseFloat(r.discountRate || 0) / 100),
                          };
                        }));
                      }}
                      module="inventory/products"
                      placeholder={t('inventory.searchProduct')}
                      createLink="/inventory/products/create"
                      canCreate
                    />
                  </td>
                  <td>
                    <Input
                      type="textarea"
                      rows="1"
                      value={data.description}
                      onChange={e => setRows(rows.map(r => r.id === data.id ? { ...r, description: e.target.value } : r))}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={data.quantity}
                      onChange={e => setRows(rows.map(r => r.id === data.id ? { ...r, quantity: e.target.value } : r))}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={data.unitPrice}
                      onChange={e => setRows(rows.map(r => r.id === data.id ? { ...r, unitPrice: e.target.value, total: e.target.value * r.quantity } : r))}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={data.total}
                      readOnly
                    />
                  </td>
                  <td>
                    <IoMdClose onClick={() => handleDeleteRow(key)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Row>
            <Col lg="7" md="6">
              <FormGroup>
                <Button color="primary" outline size="sm" onClick={handleAddRow}>+ {t('common.addNewRow')}</Button>
              </FormGroup>

              <FormGroup>
                <Label>{t('sales.notes')}: </Label>
                <Input
                  type="textarea"
                  value={invoiceData.notes}
                  onChange={e => setInvoiceData({ ...invoiceData, notes: e.target.value })}
                />
              </FormGroup>
            </Col>

            <Col lg="5" md="6">
              <div className="bg-white p-3">
                <Row className="mb-3">
                  <Col md="6">
                    <strong>{t('common.subTotal')}: </strong>
                  </Col>
                  <Col md="6" className="text-end">
                    { rows.reduce((acc, row) => acc + row.total, 0) } { companySettings?.sales?.base_currency }
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <h5 className="fw-bold">{t('common.total')}: </h5>
                  </Col>
                  <Col md="6" className="text-end">
                    { rows.reduce((acc, row) => acc + row.total, 0) } { companySettings?.sales?.base_currency }
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <FixedSave
        onSave={saveInvoice}
        loading={saveLoading}
        disabled={saveLoading || !invoiceData?.customer_id}
      />
    </SidebarLayout>
  );
}

export default CreateRecurringInvoicePage;
